/** @format */

import React, { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
  Outlet,
  useRouteError,
  isRouteErrorResponse,
} from "react-router-dom";
import "./App.scss";
// import { ReactComponent as BookLogo } from './logo_book2.svg'
import { ReactComponent as WJECLogo } from "./wjec.svg";
import { ReactComponent as CBACLogo } from "./cbac.svg";
import { ReactComponent as EduqasLogo } from "./eduqas.svg";
import { ReactComponent as WindowPlus } from "bootstrap-icons/icons/window-plus.svg";
import LocaleSwitch from "./i18n/LocaleSwitch";
import { useTranslation } from "react-i18next";
import { useLocale } from "./i18n/hooks";
import {
  selectBrand,
  setBrand,
  setSubjects,
  Subject,
} from "./features/resources/resourcesSlice";
// import { useLocalisation } from './i18n/hooks'
import { useAnalytics } from "use-analytics";
import { useDispatch, useSelector } from "react-redux";
import UserWidget from "./features/user/UserWidget";
import { ErrorResponse } from "@remix-run/router";
import { setUserToken, UserToken } from "./features/auth/authSlice";
import { Buffer } from "buffer";
import { Button } from "react-bootstrap";
import { useAppSelector } from "./app/hooks";

import TermsConditions from "./features/user/termsConditions/TermsConditions";

// i18n.changeLanguage('cy')
// const locale: Locale = i18n.resolvedLanguage as Locale

function App(): JSX.Element {
  const [Logo, setLogo] = useState<React.FC<{ className?: string }>>(WJECLogo);
  const currentLocale = useLocale();
  const { t: _ } = useTranslation();
  const { page } = useAnalytics();
  const location = useLocation();
  const dispatch = useDispatch();
  const brand = useSelector(selectBrand());
  const [searchParams, setSearchParams] = useSearchParams();
  const error = useRouteError() as ErrorResponse | undefined;
  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const navigate = useNavigate();

  /** Fix for eduqas state / querystring / domain */

  useEffect(() => {
    const tkid = searchParams.get("tkid");
    if (!!tkid) {
      const tokenKeys = ["expires", "refreshToken", "token", "service"];
      const token: UserToken = JSON.parse(
        Buffer.from(tkid, "base64").toString("utf-8"),
      );

      if (
        Object.keys(token).every((key) => tokenKeys.includes(key)) &&
        token.expires > Date.now()
      ) {
        dispatch(setUserToken(token)); // Update user token with new details
      }

      searchParams.delete("tkid");
      setSearchParams(searchParams, { replace: true });
    }
  }, [dispatch, searchParams, setSearchParams]);

  useEffect(() => {
    const hasEduqasParam = searchParams.get("eduqas") !== null;
    const isEduqasHostname = document.location.hostname.includes("eduqas");

    if (hasEduqasParam || isEduqasHostname) {
      dispatch(setBrand("eduqas"));
    } else if (brand === "eduqas" && !isEduqasHostname) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set("eduqas", "");
      setSearchParams(updatedSearchParams, { replace: true });
    }
  }, [searchParams, dispatch, brand, setSearchParams]);

  useEffect(() => {
    if (!brand) return;
    import(`./json/${brand}Subjects.json`).then(({ default: _subjects }) => {
      dispatch(setSubjects(_subjects[currentLocale] as Subject[]));
    });
  });

  useEffect(() => {
    setLogo(
      currentLocale === "cy"
        ? CBACLogo
        : brand === "eduqas"
          ? EduqasLogo
          : WJECLogo,
    );
  }, [currentLocale, brand]);

  useEffect(() => {
    page();
  }, [page, location]);

  return (
    <div className={`App ${brand}`}>
      <header>
        <div className="navbar navbar-expand-lg navbar-dark bg-dark text-light py-3">
          <div className="container-fluid">
            <Link className="navbar-brand fs-3" to="/">
              {/* <BookLogo className="d-inline-block align-middle me-4" /> */}
              {_("Resource filter")}
              {/* <span className="fw-light">{t('Educational Resources')}</span> */}
            </Link>
            <div className="ms-auto navbar-right-content">
              {isAuthenticated && (
                <Button
                  variant="white"
                  className="create-resource-btn me-4 text-editing d-flex align-items-center"
                  onClick={() => navigate("/select-resource")}
                >
                  <WindowPlus className="me-2 h-75 w-auto mx-auto" />
                  {_("Create resource")}
                </Button>
              )}
              {brand !== "eduqas" && (
                <LocaleSwitch className="d-none d-sm-inline-block locale-switch me-4" />
              )}

              <div className="d-inline-block">
                <UserWidget />
              </div>

              <a
                className="d-inline-block"
                href={`https://www.${
                  brand === "eduqas" ? "eduqas.co.uk" : _("wjec.co.uk")
                }/`}
                rel="noreferrer"
                target="_blank"
              >
                <Logo className="wjec-logo" />
              </a>
            </div>
          </div>
        </div>

        {brand !== "eduqas" && (
          <div className="container-fluid d-flex fw-bold">
            <LocaleSwitch className="d-sm-none locale-switch ms-auto" />
          </div>
        )}
      </header>

      <main className="flex-grow-1">
        <div className="content container-fluid pt-0 pt-sm-3 pb-4">
          {isRouteErrorResponse(error) && (
            <h1>
              {error.status} {error.statusText}
            </h1>
          )}
          <Outlet />
        </div>
      </main>

      <footer>
        <div className="bg-dark border-top py-2 text-start text-light">
          <div className="container-fluid">
            <small>
              &copy; {_("copyright")}&ndash;{new Date().getFullYear()}
            </small>
          </div>
        </div>
      </footer>

      {isAuthenticated && <TermsConditions />}
    </div>
  );
}

export default App;
